/**
 * ACTIVITY-CORE — human-readable feed text (i18n).
 *
 * App-SPECIFIC part (adapt when cloning): the entity_type+action →
 * phrase. Structure (patch, grouping, offline) lives in @bodega/shared.
 * Message descriptors `msg` (not strings) are returned so the language is reactive.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { type ActivityRow, parseActivityPatch } from "@bodega/shared";

interface ActivitySentence {
  /** Phrase with `{name}` interpolated. */
  named: MessageDescriptor;
  /** Phrase without a name (the patch does not contain one). */
  anonymous: MessageDescriptor;
}

/**
 * Fine semantic key: refines `updated`/`deleted` with what actually happened
 * happened (complete, move to trash…) by looking at the patch.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function activitySemanticKey(row: ActivityRow): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  // Sync audit (Bodega): refine by the outcome recorded in the patch.
  if (row.entity_type === "sync") {
    const status = patch.status?.new;
    return typeof status === "string" && status.length > 0 ? status : "completed";
  }
  if (row.entity_type === "todo" && action === "updated" && patch.completed) {
    return patch.completed.new ? "completed" : "reopened";
  }
  if (action === "deleted") {
    // deleted_at transition = trash; full snapshot = permanent purge.
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

const TODO_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the task "{name}"`, anonymous: msg`added a task` },
  completed: { named: msg`completed the task "{name}"`, anonymous: msg`completed a task` },
  reopened: { named: msg`reopened the task "{name}"`, anonymous: msg`reopened a task` },
  updated: { named: msg`edited the task "{name}"`, anonymous: msg`edited a task` },
  trashed: {
    named: msg`moved the task "{name}" to the trash`,
    anonymous: msg`moved a task to the trash`,
  },
  purged: {
    named: msg`permanently deleted the task "{name}"`,
    anonymous: msg`permanently deleted a task`,
  },
  restored: { named: msg`restored the task "{name}"`, anonymous: msg`restored a task` },
  reverted: {
    named: msg`undid a change on the task "{name}"`,
    anonymous: msg`undid a change on a task`,
  },
};

const LIST_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`created the list "{name}"`, anonymous: msg`created a list` },
  updated: { named: msg`edited the list "{name}"`, anonymous: msg`edited a list` },
  trashed: {
    named: msg`moved the list "{name}" to the trash`,
    anonymous: msg`moved a list to the trash`,
  },
  purged: {
    named: msg`permanently deleted the list "{name}"`,
    anonymous: msg`permanently deleted a list`,
  },
  restored: { named: msg`restored the list "{name}"`, anonymous: msg`restored a list` },
  reverted: {
    named: msg`undid a change on the list "{name}"`,
    anonymous: msg`undid a change on a list`,
  },
};

// Subtasks have no trash: their possible semantic keys are
// created / updated / purged (hard deletion) / reverted.
const SUBTASK_TEXTS: Record<string, ActivitySentence> = {
  created: { named: msg`added the subtask "{name}"`, anonymous: msg`added a subtask` },
  updated: { named: msg`edited the subtask "{name}"`, anonymous: msg`edited a subtask` },
  purged: { named: msg`deleted the subtask "{name}"`, anonymous: msg`deleted a subtask` },
  reverted: {
    named: msg`undid a change on the subtask "{name}"`,
    anonymous: msg`undid a change on a subtask`,
  },
};

// Sync audit (Bodega P-1): what was synced, to which backend, success/failure.
// The semantic key is the job's `status` (completed/failed/conflict-resolved/
// restored); `{name}` is the synchronized folder.
const SYNC_TEXTS: Record<string, ActivitySentence> = {
  completed: { named: msg`synced "{name}"`, anonymous: msg`completed a sync` },
  failed: { named: msg`failed to sync "{name}"`, anonymous: msg`a sync failed` },
  "conflict-resolved": {
    named: msg`resolved a conflict in "{name}"`,
    anonymous: msg`resolved a sync conflict`,
  },
  restored: {
    named: msg`restored a file in "{name}"`,
    anonymous: msg`restored a synced file`,
  },
};

const FALLBACK: ActivitySentence = {
  named: msg`changed "{name}"`,
  anonymous: msg`made a change`,
};

/** Visible entity name extracted from patch (text/name/folder, new or old). */
export function activityEntityName(row: ActivityRow): string | null {
  const patch = parseActivityPatch(row.patch);
  for (const key of ["text", "name", "folder"] as const) {
    const change = patch[key];
    if (!change) {
      continue;
    }
    const value = change.new ?? change.old;
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * i18n phrase for an entry: descriptor + values. Render with
 * `t(descriptor, values)` (or i18n._) in the component.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function activitySentence(row: ActivityRow): {
  descriptor: MessageDescriptor;
  values: Record<string, string>;
} {
  const key = activitySemanticKey(row);
  const table =
    row.entity_type === "todo"
      ? TODO_TEXTS
      : row.entity_type === "list"
        ? LIST_TEXTS
        : row.entity_type === "subtask"
          ? SUBTASK_TEXTS
          : row.entity_type === "sync"
            ? SYNC_TEXTS
            : {};
  const sentence = table[key] ?? FALLBACK;
  const name = activityEntityName(row);
  if (name) {
    return { descriptor: sentence.named, values: { name } };
  }
  return { descriptor: sentence.anonymous, values: {} };
}

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;

type RelativeUnit = "second" | "minute" | "hour" | "day";

const UNITS: Record<RelativeUnit, [singular: string, plural: string]> = {
  second: ["second", "seconds"],
  minute: ["minute", "minutes"],
  hour: ["hour", "hours"],
  day: ["day", "days"],
};

/**
 * Pure relative-time copy. `Intl.RelativeTimeFormat` (and `toLocaleDateString`)
 * can deadlock in Node/ICU inside Vitest workers on the CI runner — a 5s
 * timeout on this formatter is a hang, not slowness.
 *
 * Copy is English on purpose: wiring these through Lingui would reintroduce a
 * catalog/runtime dependency in a function that must stay synchronous.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
function formatRelativeLabel(delta: number, unit: RelativeUnit): string {
  const count = Math.abs(delta);
  const word = count === 1 ? UNITS[unit][0] : UNITS[unit][1];
  return delta > 0 ? `in ${count} ${word}` : `${count} ${word} ago`;
}

function formatAbsoluteDate(ts: number): string {
  const d = new Date(ts);
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** English "5 minutes ago"; falls back to a UTC date past one week. */
export function formatRelativeTime(
  iso: string | null,
  _locale: string,
  now: Date = new Date(),
): string {
  if (!iso) {
    return "";
  }
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) {
    return "";
  }
  const diff = ts - now.getTime();
  const abs = Math.abs(diff);
  if (abs < MINUTE_MS) {
    return formatRelativeLabel(Math.round(diff / SECOND_MS), "second");
  }
  if (abs < HOUR_MS) {
    return formatRelativeLabel(Math.round(diff / MINUTE_MS), "minute");
  }
  if (abs < DAY_MS) {
    return formatRelativeLabel(Math.round(diff / HOUR_MS), "hour");
  }
  if (abs < WEEK_MS) {
    return formatRelativeLabel(Math.round(diff / DAY_MS), "day");
  }
  return formatAbsoluteDate(ts);
}
